import React, { useEffect, useState, createContext, useContext } from 'react'
import { JsonForms } from '@jsonforms/react'
import $RefParser from '@apidevtools/json-schema-ref-parser'
import { useTheme, StyledEngineProvider, Typography, Box } from '@mui/material'
import { ThemeProvider } from '@mui/material/styles'
import { materialCells, materialRenderers } from '@jsonforms/material-renderers'
import { createAjv } from '@jsonforms/core'
import { get, merge } from 'lodash-es'

import CustomEnumControl from './controls/CustomEnumControl'
import CustomEnumControlTester from './controls/CustomEnumControlTester'
import CustomEnumCell from './controls/CustomEnumCell'
import CustomLabel from './controls/CustomLabel'
import CustomLabelTester from './controls/CustomLabelTester'
import Divider from './controls/Divider'
import DividerTester from './controls/DividerTester'
import MultipleSelectFieldControl from './controls/MultipleSelectFieldControl'
import MultipleSelectFieldControlTester from './controls/MultipleSelectFieldControlTester'
import FileInputControl from './controls/FileInputControl'
import FileInputControlTester from './controls/FileInputControlTester'
import StandaloneFileInputControl from './controls/StandaloneFileInputControl'
import SubtitleControl from './controls/Subtitle'
import SubtitleControlTester from './controls/SubtitleTester'
import DateControl from './controls/DateControl'
import DateControlTester from './controls/DateControlTester'
import SectionLayout, { sectionTester } from './controls/SectionLayout'
import DynamicOneOfControl, {
    dynamicOneOfControlTester,
} from './controls/DynamicOneOfControl'
import DynamicOneOfCellControl from './controls/DynamicOneOfCellControl'
import DependentSelectControl, {
    DependentSelectControlTester,
} from './controls/DependentSelectControl'
import DependentDynamicCellControl from './controls/DependentCellControl'
import LinkWithCopyControl from './controls/LinkWithCopyControl'
import LinkWithCopyControlTester from './controls/LinkWithCopyControlTester'
import TextWithCopyControl from './controls/TextWithCopyControl'
import TextWithCopyControlTester from './controls/TextWithCopyControlTester'

const DynamicConfigContext = createContext()

const DynamicConfigContextProvider = ({ value, children }) => (
    <DynamicConfigContext.Provider value={value}>
        {children}
    </DynamicConfigContext.Provider>
)

export const useDynamicConfigs = () => useContext(DynamicConfigContext)

const PreviewModeContext = createContext(false)

export const usePreviewMode = () => useContext(PreviewModeContext)

export const jsonFormTheme = {
    components: {
        MuiFormControl: {
            defaultProps: {
                variant: 'standard',
            },
        },
        MuiTextField: {
            defaultProps: {
                variant: 'standard',
            },
        },
        MuiSelect: {
            defaultProps: {
                variant: 'standard',
            },
        },
    },
    overrides: {
        MuiFormControl: {
            root: {
                minWidth: '15em',
                marginBottom: '10px',
            },
        },
        MuiInputLabel: {
            root: {
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                width: '100%',
                whiteSpace: 'nowrap',
            },
        },
    },
    jsonforms: {
        input: {
            delete: {
                background: '#ffffff',
            },
        },
    },
}

// In React 18, error boundaries are still class components.
export class JsonFormsErrorBoundary extends React.Component {
    constructor(props) {
        super(props)
        this.state = { hasError: false, error: null }
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error }
    }

    render() {
        if (this.state.hasError) {
            return (
                <Box
                    sx={{
                        padding: '10px',
                        background: '#f5f5f5',
                        width: '75%',
                    }}
                >
                    <Typography>
                        An error occurred while rendering the form:
                    </Typography>
                    <Typography color="error">
                        {this.state.error?.message}
                    </Typography>
                </Box>
            )
        }

        return this.props.children
    }
}

// TODO: AdvancedMapperControl is intentionally NOT registered here. It pulls
// in final-form, mui-rff, and react-final-form-* deps that cliconfigs
// shouldn't bundle. PandipackV2 wraps this component and injects
// AdvancedMapperControl via `extraRenderers`. When AdvancedMapper is rewritten
// dependency-free (separate ticket), move it into this package and register
// it here directly. That also closes the cliconfigs preview gap
// (cliconfigs currently can't render `AdvancedMapper`-typed fields).
//
// Several controls also still import from `react-admin` (FileInputControl,
// DependentSelectControl, CustomStringInput). Removing those is another
// future ticket.
export const JsonFormsWrapper = ({
    schema,
    uischema,
    onJsonFormChange,
    data = {},
    readonly = false,
    fullWidth,
    dynamicConfigs = {},
    mode = 'internal',
    previewMode = false,
    extraRenderers = [],
    extraCells = [],
}) => {
    const updateData = (errors, data) => {
        onJsonFormChange(errors, data)
    }

    // resolves refs. needed for the advanced mapper.
    const [resolvedSchema, setSchema] = useState()
    useEffect(() => {
        $RefParser.dereference(schema).then((res) => setSchema(res))
    }, [schema])

    const ajv = createAjv({ useDefaults: true, coerceTypes: true })

    // custom AJV keyword `requireNotEmpty` rejects blank string inputs
    // (whitespace-only values fail validation). Auto-attached to all `required`
    // string fields below.
    ajv.addKeyword({
        keyword: 'requireNotEmpty',
        type: 'string',
        validate: function validate(_schema, data) {
            validate.errors = [
                {
                    keyword: 'requireNotEmpty',
                    message: 'Field must not be empty.',
                    params: { keyword: 'requireNotEmpty' },
                },
            ]
            return data.trim() !== ''
        },
        errors: true,
    })

    // Kept outside useEffect so a memoized parent doesn't see a stale schema.
    if (get(resolvedSchema, 'required')) {
        resolvedSchema.required.forEach((item) => {
            const schemaPropItem = get(resolvedSchema, `properties.${item}`)
            if (
                schemaPropItem &&
                schemaPropItem.type === 'string' &&
                get(schemaPropItem, 'requireNotEmpty') === undefined
            ) {
                merge(resolvedSchema.properties[item], {
                    requireNotEmpty: true,
                })
            }
        })
    }

    useEffect(() => {
        if (!fullWidth) {
            uischema.elements.forEach((element, idx) => {
                if (
                    element.type === 'Control' &&
                    get(element, 'options.trim') === undefined
                ) {
                    const matchingSchema = get(
                        resolvedSchema,
                        'properties.' + element.scope.split('/')[2]
                    )

                    if (get(matchingSchema, 'type') === 'string') {
                        merge(uischema.elements[idx], {
                            options: { trim: true },
                        })
                    }
                }
            })
        }
    }, [fullWidth, uischema, resolvedSchema])

    const mainTheme = useTheme()
    const newTheme = {}
    Object.assign(newTheme, mainTheme, jsonFormTheme)

    const fileInputRenderer =
        mode === 'external' ? StandaloneFileInputControl : FileInputControl

    return (
        <JsonFormsErrorBoundary>
            {resolvedSchema && (
                <PreviewModeContext.Provider value={previewMode}>
                    <DynamicConfigContextProvider value={dynamicConfigs}>
                        <StyledEngineProvider injectFirst>
                            <ThemeProvider theme={newTheme}>
                                {ajv.validateSchema(resolvedSchema) ? (
                                    <JsonForms
                                        data={data}
                                        schema={resolvedSchema}
                                        uischema={uischema}
                                        readonly={readonly}
                                        validationMode="ValidateAndHide"
                                        renderers={[
                                            ...materialRenderers,
                                            {
                                                tester: CustomEnumControlTester,
                                                renderer: CustomEnumControl,
                                            },
                                            {
                                                tester: sectionTester,
                                                renderer: SectionLayout,
                                            },
                                            {
                                                tester: CustomLabelTester,
                                                renderer: CustomLabel,
                                            },
                                            {
                                                tester: DividerTester,
                                                renderer: Divider,
                                            },
                                            {
                                                tester: MultipleSelectFieldControlTester,
                                                renderer: MultipleSelectFieldControl,
                                            },
                                            {
                                                tester: FileInputControlTester,
                                                renderer: fileInputRenderer,
                                            },
                                            {
                                                tester: SubtitleControlTester,
                                                renderer: SubtitleControl,
                                            },
                                            {
                                                tester: DateControlTester,
                                                renderer: DateControl,
                                            },
                                            {
                                                tester: dynamicOneOfControlTester,
                                                renderer: DynamicOneOfControl,
                                            },
                                            {
                                                tester: LinkWithCopyControlTester,
                                                renderer: LinkWithCopyControl,
                                            },
                                            {
                                                tester: TextWithCopyControlTester,
                                                renderer: TextWithCopyControl,
                                            },
                                            {
                                                tester: DependentSelectControlTester,
                                                renderer: DependentSelectControl,
                                            },
                                            ...extraRenderers,
                                        ]}
                                        cells={[
                                            ...materialCells,
                                            {
                                                tester: dynamicOneOfControlTester,
                                                cell: DynamicOneOfCellControl,
                                            },
                                            {
                                                tester: DependentSelectControlTester,
                                                cell: DependentDynamicCellControl,
                                            },
                                            {
                                                tester: CustomEnumControlTester,
                                                cell: CustomEnumCell,
                                            },
                                            ...extraCells,
                                        ]}
                                        onChange={({ errors, data }) =>
                                            updateData(errors, data)
                                        }
                                        ajv={ajv}
                                    />
                                ) : (
                                    <p>
                                        There is an error within your configs.
                                        Please contact an admin to fix this.
                                    </p>
                                )}
                            </ThemeProvider>
                        </StyledEngineProvider>
                    </DynamicConfigContextProvider>
                </PreviewModeContext.Provider>
            )}
        </JsonFormsErrorBoundary>
    )
}
